import { defaultRegion } from 'boot/firebase';
import posthog from 'posthog-js';
import { boot } from 'quasar/wrappers';

const deadClickUrlAllowlist = [
  /#\/performance\/care-model(?:$|\?)/,
  /#\/performance\/care-model\/[^/?]+(?:$|\?)/,
  /#\/settings\/care-model\/cohorts(?:$|\?)/,
  /#\/settings\/learnings-moderation(?:$|\?)/,
];

/**
 * @param {Object} event - PostHog event passed through before_send.
 * @returns {Object|null} The event to send, or null to drop it.
 */
function prepareDeadClickEvent(event) {
  const isFromAnalyzedPage = deadClickUrlAllowlist.some(
    (pattern) => pattern.test(event.properties.$current_url || ''),
  );
  const elementText = event.properties.$el_text;

  if (!isFromAnalyzedPage || !elementText) {
    return null;
  }

  event.properties.elementText = elementText;
  return event;
}

let isPosthogEnabled = false;

export default boot(({ app }) => {
  // PostHog is currently only provides services in the US and EU regions and we only use US region for
  // analytics on production and EU on staging (we currently use same DSN for both regions).
  const allowedRegions = {
    development: ['eu'],
    staging: ['eu'],
    production: ['us'],
  };

  const region = localStorage.getItem('selected_region') || defaultRegion;
  const platform = process.env.MODE === 'capacitor' ? 'mobile' : 'web';
  const offlineModeStatus = JSON.parse(localStorage.getItem('offlineModeStatus')) || false;
  const release = process.env.version || '?';
  const environment = process.env.ENV_TYPE;

  const postHogConfig = {};
  switch (region) {
    case 'us':
      postHogConfig.key = process.env.us.POSTHOG_KEY;
      postHogConfig.apiHost = process.env.us.POSTHOG_DSN;
      break;
    case 'eu':
      postHogConfig.key = process.env.eu.POSTHOG_KEY;
      postHogConfig.apiHost = process.env.eu.POSTHOG_DSN;
      break;
    case 'ca':
      postHogConfig.key = process.env.ca.POSTHOG_KEY;
      postHogConfig.apiHost = process.env.ca.POSTHOG_DSN;
      break;
    case 'au':
      postHogConfig.key = process.env.au.POSTHOG_KEY;
      postHogConfig.apiHost = process.env.au.POSTHOG_DSN;
      break;
    default:
      isPosthogEnabled = false;
      return;
  }

  if (!allowedRegions[environment]?.includes(region)) {
    isPosthogEnabled = false;
    return;
  }

  posthog.init(
    postHogConfig.key || '',
    {
      api_host: postHogConfig.apiHost || '',
      defaults: '2026-01-30',
      autocapture: false,
      capture_pageview: false,
      capture_dead_clicks: true,
      remote_config_refresh_interval_ms: 0,
      advanced_disable_feature_flags_on_first_load: true,
      before_send: (rawEvent) => {
        const event = rawEvent.event === '$dead_click' ? prepareDeadClickEvent(rawEvent) : rawEvent;

        if (!event) {
          return null;
        }

        event.properties.platform = platform;
        event.properties.appVersion = release;
        event.properties.environment = environment;
        event.properties.offline = offlineModeStatus;
        return event;
      },
    },
  );

  isPosthogEnabled = true;
});

export function getIsPosthogEnabled() {
  return isPosthogEnabled;
}

export { posthog };
